import { FlowNodeJSON } from '@flowgram.ai/free-layout-editor';

export interface CreateNodeExecuteTaskResult {
  task_id: string;
  estimated_total_ms: number; // 预估总耗时，单位毫秒
}

export interface NodeType extends FlowNodeJSON {
  type: FlowNodeTypeEnum;
  data: {
    // 搜索下拉框的下拉数据-为了数据回显
    mcpCategoriesId: string;
    apiName: string;
    parameters: {
      // 表单框的key和值
      [key: string]: string | number | boolean;
    };
    isGenerated?: boolean;
    parentNodeId?: string;
    childrenNodeList?: string[];
    unfinishTaskList?: any[];
  };
}

export enum FlowPartType {
  IMAGE = 'IMAGE',
  TEXT = 'TEXT',
  VIDEO = 'VIDEO',
  GIF = 'GIF',
  AUDIO = 'AUDIO',
}
export enum FlowNodeTypeEnum {
  IMAGE_NODE = 'IMAGE_NODE',
  GIF_NODE = 'GIF_NODE',
  TEXT_NODE = 'TEXT_NODE',
  VIDEO_NODE = 'VIDEO_NODE',
  AUDIO_NODE = 'AUDIO_NODE',
  TEXT_EDIT_NODE = 'TEXT_EDIT_NODE',
  IMAGE_EDIT_NODE = 'IMAGE_EDIT_NODE',
  VIDEO_EDIT_NODE = 'VIDEO_EDIT_NODE',
  GIF_EDIT_NODE = 'GIF_EDIT_NODE',
  AUDIO_EDIT_NODE = 'AUDIO_EDIT_NODE',
}

// 1 图片 2 视频 4 gif 5 文本 9 音频
export const reusltTypeMap = {
  1: FlowNodeTypeEnum.IMAGE_EDIT_NODE,
  2: FlowNodeTypeEnum.VIDEO_EDIT_NODE,
  4: FlowNodeTypeEnum.GIF_EDIT_NODE,
  5: FlowNodeTypeEnum.TEXT_EDIT_NODE,
  9: FlowNodeTypeEnum.AUDIO_EDIT_NODE,
};

export const FlowNodeGenerateNodeTypeMap: Record<string, FlowNodeTypeEnum> = {
  TEXT_EDIT_NODE: FlowNodeTypeEnum.TEXT_NODE,
  IMAGE_EDIT_NODE: FlowNodeTypeEnum.IMAGE_NODE,
  VIDEO_EDIT_NODE: FlowNodeTypeEnum.VIDEO_NODE,
  GIF_EDIT_NODE: FlowNodeTypeEnum.GIF_NODE,
  AUDIO_EDIT_NODE: FlowNodeTypeEnum.AUDIO_NODE,
};

export enum WorkflowNodeType {
  Start = 'start',
  End = 'end',
  LLM = 'llm',
  HTTP = 'http',
  Code = 'code',
  Variable = 'variable',
  Condition = 'condition',
  Loop = 'loop',
  BlockStart = 'block-start',
  BlockEnd = 'block-end',
  Comment = 'comment',
  Continue = 'continue',
  Break = 'break',
}

export enum FlowPortTypeColor {
  TEXT = '#7f66ff',
  IMAGE = '#E65CE6',
  VIDEO = '#4698EB',
  AUDIO = '#1FC8A9',
}

export enum FlowPortTypeLabel {
  TEXT = '文本',
  IMAGE = '图片',
  VIDEO = '视频',
  AUDIO = '音频',
}

export enum SlideTabEnum {
  featuredWorkflow = 'choice',
  myNode = 'mine',
}

export interface Extra {
  big_data_patch?: {
    position_type: '/team_studio';
  };
  /** 标识 nodeexecute 的业务执行来源，智能补全无需工作流分组信息。 */
  snapshot: {
    group_id: string;
    group_type: 'normal' | 'storyboard' | 'ip';
    /** IP 组内节点执行时，关联的 IP 身份。 */
    ip_id?: string;
    tool_type: 'mcp' | 'model' | 'gen_role';
  } | {
    tool_type: 'smart_completion';
  };
}

export type NodeExecuteTaskSpeed = '1x' | '1.5x';

/**
 * 创建节点任务入参
 *
 * @export
 * @interface NodeExecuteTaskParams
 */
export interface NodeExecuteTaskParams {
  name: string;
  tree_id?: string;
  tool_abstract_name: { 'cn': string; en: string; };
  node_id?: string;
  extra?: Extra;
  /** 节点任务自身的回显扩展数据，随 node_list_array 单项提交。 */
  node_extra?: Record<string, any>;
  parameters: {
    [key: string]: any;
  };
  /** 主工具执行前依次调用的前置工具；视频解析等单阶段工具不发送该字段。 */
  pre_tools?: Array<{
    name: string;
    parameters: {
      [key: string]: any;
    };
  }>;
  /** 前置工具需要提示词优化协议时发送；无前置工具时不发送。 */
  pre_type?: 'prompt_optimize';
}

/**
 * 创建节点执行任务入参
 */
export interface CreateNodeExecuteTaskOptions {
  room_id: string;
  /** 同一业务批次可在多次独立 nodeexecute 请求间复用该 ID；未传时自动生成。 */
  group_id?: string;
  /**
   * 单任务、多个独立任务，或同一执行链内的多个工具。
   * 二维数组会原样映射为 node_list_array，供一个 group_id 下的工具链使用。
   */
  params: NodeExecuteTaskParams | NodeExecuteTaskParams[] | NodeExecuteTaskParams[][];
  passThroughTaskId?: string;
  workflow_version?: 'v0' | 'v1' | 'v2';
  extra?: Extra;
  business_type?: string;
  taskSpeed?: NodeExecuteTaskSpeed;
  media_compress_url_map?: Record<string, string>;
}

export interface OutputData {
  data: {
    result: {
      parameters: any;
      images: string[];
      urls: string[];
      media_info_list: {
        media_data: string;
      }[];
    };
  };
}

interface TaskItem {
  state: 'SUCCESS' | 'FAIL' | string;
  output: string;
  /** 片段重拍工具在 step 层返回的逐段执行状态。 */
  segments?: NodeExecuteTaskSegment[];
  /** output 原始 URL 到已转存 URL 的映射。 */
  transfer?: Record<string, string>;
  /** 转存服务返回的原始 URL 到正式 HTTPS URL 映射。 */
  transferred_url_map?: Record<string, string>;
  cost?: number;
  fail_code?: string;
  name: string;
  error_message?: string;
  params: Record<string, any>;
  node_extra?: Record<string, any>;
}

/** 片段类任务查询返回的单段执行结果，时间单位为秒。 */
export interface NodeExecuteTaskSegment {
  id: string;
  msg_id?: string;
  start: number;
  end: number;
  status: string;
}

/**
 * 创建节点任务返回数据
 *
 * @export
 * @interface NodeExecuteTaskResult
 */
export interface NodeExecuteTaskResult {
  tasks: {
    [key: string]: {
      state?: string;
      steps: TaskItem[];
      last_image_urls: string[];
      last_video_url: string;
      initial_transferred_urls: string[];
      /** 任务转存阶段生成的视频封面，与 initial_transferred_urls 按顺序对应。 */
      initial_transferred_cover_urls?: string[];
      initial_transferred_compress_urls?: string[];
      last_image_compress_urls?: string[];
      /** 媒体分离工具返回的结果 URL，当前协议按固定顺序解析并兼容历史文件名。 */
      split_list?: string[];
      /** 兼容任务级返回的原始 URL 到已转存 URL 映射。 */
      transfer?: Record<string, string>;
      /** 任务级转存结果，媒体节点必须优先使用这里的正式 HTTPS URL。 */
      transferred_url_map?: Record<string, string>;
      error_message?: string;
      media_meta: {
        width: number;
        height: number;
        duration?: number; // 视频时长，单位秒
      };
      media_metas: {
        width: number;
        height: number;
        duration?: number; // 视频时长，单位秒
      }[];
      /** 后端根据 need_node_name 生成的节点展示名 */
      node_name?: string;
      /** 片段重拍等任务返回的逐段执行状态。 */
      segments?: NodeExecuteTaskSegment[];
    };
  };
  current_time_ms: number;
}

export interface NodeExecuteHistoryNodeItem {
  index?: number;
  name?: string;
  state?: string;
  params?: Record<string, unknown>;
  output?: string;
  error_code?: number;
  error_message?: string;
}

export interface NodeExecuteHistoryTaskItem {
  task_id: string;
  state?: string;
  create_time_ms?: number;
  end_time_ms?: number;
  total_duration_ms?: number;
  initial_transferred_urls?: string[];
  initial_transferred_cover_urls?: string[];
  initial_transferred_compress_urls?: string[];
  node_list?: NodeExecuteHistoryNodeItem[];
}

export interface NodeExecuteHistoryResult {
  tasks: NodeExecuteHistoryTaskItem[];
  total_count: number;
  days: number;
  business_type?: string;
  uid?: string;
  mcp_name?: string;
}

export interface NodeListItem {
  node_id: number;
  name: string;
  cover: string;
  data: string;
  client_status: number;
  width?: number;
  height?: number;
}

// mcp工具输入输出的节点
export enum InputOutputEnum {
  Text = 'text',
  Image = 'image',
  Video = 'video',
  Gif = 'gif',
  Audio = 'audio',
}

// 节点生成类型转换映射
// 感觉这么写不是很合理，但是产品只能给这个🤷🏻后续再优化吧
export const NodeCreateTypeMap = new Map([
  [InputOutputEnum.Text, FlowNodeTypeEnum.TEXT_EDIT_NODE],
  [InputOutputEnum.Image, FlowNodeTypeEnum.IMAGE_EDIT_NODE],
  [InputOutputEnum.Video, FlowNodeTypeEnum.VIDEO_EDIT_NODE],
  [InputOutputEnum.Gif, FlowNodeTypeEnum.GIF_EDIT_NODE],
  [InputOutputEnum.Audio, FlowNodeTypeEnum.AUDIO_EDIT_NODE],
]);

// 输入节点生成类型转换映射
export const InputNodeCreateTypeMap = new Map([
  [InputOutputEnum.Text, FlowNodeTypeEnum.TEXT_NODE],
  [InputOutputEnum.Image, FlowNodeTypeEnum.IMAGE_NODE],
  [InputOutputEnum.Video, FlowNodeTypeEnum.VIDEO_NODE],
  [InputOutputEnum.Audio, FlowNodeTypeEnum.AUDIO_NODE],
]);

// 通过FlowPartType 映射到mcp工具可以输入（targetTag）的类型
export const FlowPartTypeToInputTagMap = new Map([
  [FlowPartType.TEXT, InputOutputEnum.Text],
  [FlowPartType.IMAGE, InputOutputEnum.Image],
  [FlowPartType.VIDEO, InputOutputEnum.Video],
  [FlowPartType.GIF, InputOutputEnum.Gif],
  [FlowPartType.AUDIO, InputOutputEnum.Audio],
]);

export const FlowPartTypeToNodeTypeMap = new Map([
  [FlowPartType.TEXT, FlowNodeTypeEnum.TEXT_NODE],
  [FlowPartType.IMAGE, FlowNodeTypeEnum.IMAGE_NODE],
  [FlowPartType.VIDEO, FlowNodeTypeEnum.VIDEO_NODE],
  [FlowPartType.GIF, FlowNodeTypeEnum.GIF_NODE],
]);
